// SPDX-FileCopyrightText: 2025 Slant Tech LLC
// SPDX-License-Identifier: GPL-3.0-or-later

import { useState, memo, useMemo } from 'react';
import Markdown from 'react-markdown';

const CommentScreenshot = memo(function CommentScreenshot({ content, gitlabUrl, projectId }) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const src = useMemo(() => {
    const match = (content || '').match(/!\[[^\]]*\]\(([^)]+)\)/);
    if (!match) return null;
    const raw = match[1];
    return raw.startsWith('/uploads/') && gitlabUrl && projectId
      ? `${gitlabUrl}/-/project/${projectId}${raw}`
      : raw;
  }, [content, gitlabUrl, projectId]);

  if (!src || errored) return null;
  return (
    <img
      src={src}
      alt="screenshot"
      onLoad={() => setLoaded(true)}
      onError={() => setErrored(true)}
      style={{ maxWidth: '100%', marginTop: '8px', display: loaded ? 'block' : 'none' }}
    />
  );
});

function CommentCard({
  comment,
  index,
  isSelected,
  isEditing,
  onClick,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onResolve,
  onDelete,
  gitlabUrl = '',
  projectId,
}) {
  // Use content field (backend uses content, not body)
  const [editBody, setEditBody] = useState(comment.content || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleSave = () => {
    if (editBody.trim()) {
      onSaveEdit(editBody);
    }
  };

  const handleDelete = () => {
    if (showDeleteConfirm) {
      onDelete();
    } else {
      setShowDeleteConfirm(true);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get position (backend uses position_x/position_y)
  const posX = comment.position_x ?? comment.x ?? 0;
  const posY = comment.position_y ?? comment.y ?? 0;

  return (
    <div
      className={`comment-card cursor-pointer ${comment.resolved ? 'resolved' : ''} ${
        isSelected ? 'ring-2 ring-st-accent' : ''
      }`}
      onClick={!isEditing ? onClick : undefined}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 flex items-center justify-center text-xs font-display font-semibold ${
            comment.resolved ? 'bg-st-success' : 'bg-st-accent'
          } text-white`}>
            {index}
          </span>
          <span className={`badge ${comment.resolved ? 'badge-resolved' : 'badge-open'}`}>
            {comment.resolved ? 'Resolved' : 'Open'}
          </span>
        </div>
        {comment.component_ref && (
          <span className="font-mono text-xs text-st-accent bg-st-secondary px-2 py-1">
            {comment.component_ref}
          </span>
        )}
      </div>

      {/* Body */}
      {isEditing ? (
        <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
          <textarea
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            className="input text-sm"
            rows={3}
            autoFocus
          />
          <div className="flex gap-2">
            <button onClick={handleSave} className="btn btn-primary text-xs py-1">
              Save
            </button>
            <button onClick={onCancelEdit} className="btn btn-secondary text-xs py-1">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-st-text font-body">
          <Markdown
            allowedElements={['p', 'strong', 'em', 'a', 'code', 'br', 'ul', 'ol', 'li']}
            components={{
              a: ({ href, children }) => (
                <a href={href} target="_blank" rel="noopener noreferrer" className="text-st-accent hover:text-st-accent-hover underline">{children}</a>
              ),
              p: ({ children }) => (
                <p className="mb-1 last:mb-0">{children}</p>
              ),
            }}
          >
            {(comment.content || '')
              .replace(/!\[[^\]]*\]\([^)]*\)(\{[^}]*\})?/g, '')
              .replace(/<!--.*?-->/gs, '')
              .trim()}
          </Markdown>
          <CommentScreenshot content={comment.content} gitlabUrl={gitlabUrl} projectId={projectId} />
        </div>
      )}

      {/* Footer */}
      <div className="mt-3 pt-2 border-t border-st-border-subtle flex items-center justify-between">
        <div className="text-xs text-st-muted font-mono">
          {comment.author && <span className="mr-2">{comment.author}</span>}
          <span>{formatDate(comment.created_at)}</span>
        </div>

        {/* Actions */}
        {!isEditing && (
          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => onResolve(!comment.resolved)}
              className="p-1.5 text-st-muted hover:text-st-success hover:bg-st-success/10 transition-colors"
              title={comment.resolved ? 'Unresolve' : 'Resolve'}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
            <button
              onClick={onEdit}
              className="p-1.5 text-st-muted hover:text-st-accent hover:bg-st-accent/10 transition-colors"
              title="Edit"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={handleDelete}
              className={`p-1.5 transition-colors ${
                showDeleteConfirm
                  ? 'bg-st-error/20 text-st-error'
                  : 'text-st-muted hover:text-st-error hover:bg-st-error/10'
              }`}
              title={showDeleteConfirm ? 'Click again to confirm' : 'Delete'}
              onBlur={() => setShowDeleteConfirm(false)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Position info (collapsible detail) */}
      {isSelected && (
        <div className="mt-2 pt-2 border-t border-st-border-subtle text-xs text-st-muted font-mono">
          <div className="flex items-center gap-4">
            <span><i className="bx bx-map-pin"></i> Position: ({posX.toFixed(1)}, {posY.toFixed(1)})</span>
            <span><i className="bx bx-file"></i> {comment.file_type || comment.file_path?.split('.').pop()}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default CommentCard;
